import { useEffect, useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import { type TranslatableExportSet } from './ContentItemEditor.types';

// The export set reports progress by writing to its own fields, and it is an Angular SuperModel, so
// there is nothing to subscribe to. Reading it on a timer is what keeps the status line moving.
const POLL_MS = 250;

type Props = {
    // Null until the admin asks for an export. A new instance starts a new export.
    exportSet: TranslatableExportSet | null;
};

/*
    TranslatableLessonExportSet stays in Angular, and so does TranslatableLessonExport, which builds
    each file by compiling an Angular template. Only this status and download wrapper is React.
*/
export default function TranslatableExportSetStatus({ exportSet }: Props) {
    const [status, setStatus] = useState({ preparing: false, prepared: false, statusMessage: '' });

    useEffect(() => {
        if (!exportSet) {
            setStatus({ preparing: false, prepared: false, statusMessage: '' });
            return undefined;
        }

        const read = () =>
            setStatus({
                preparing: exportSet.preparing,
                prepared: exportSet.prepared,
                statusMessage: exportSet.statusMessage,
            });

        exportSet.prepare().catch(() => {
            // The set alerts on its own. Stop following it.
            read();
        });

        read();
        const interval = setInterval(read, POLL_MS);

        return () => clearInterval(interval);
    }, [exportSet]);

    if (!exportSet) return null;

    return (
        <Stack spacing={1} sx={{ alignItems: 'flex-start' }}>
            {status.preparing && !status.prepared && (
                <Typography variant="body2" data-id="export-status">
                    {status.statusMessage}
                </Typography>
            )}

            {status.prepared && (
                <Button
                    name="download"
                    variant="contained"
                    color="primary"
                    onClick={() => exportSet.downloadTranslatableArtifacts()}
                >
                    Download
                </Button>
            )}
        </Stack>
    );
}
